import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "./../components/Layout/Layout";
import useTypewriter from "../hooks/useTypeWriter";
import MostViewedProducts from "./MostViewed";
import AutoBannerSlider from "./AutoBannerSlider";
import JustArrivedProducts from "./JustArrivedProducts";

const FeaturedCategories = ({ categories = [], onClickCategory }) => {
  return (
    <section className="w-full">
      <div
        className="
        flex overflow-x-auto gap-4 pb-6 snap-x snap-mandatory scrollbar-hide 
        lg:grid lg:grid-cols-3 lg:gap-x-8 lg:gap-y-12 lg:overflow-visible lg:pb-0
      "
      >
        {categories.map((cat) => {
          const imageUrl = cat.image
            ? `${process.env.REACT_APP_API}/${cat.image}`
            : "/images/fallback-RB.png";

          return (
            <button
              key={cat._id}
              type="button"
              onClick={() => onClickCategory?.(cat)}
              className="
                group flex-shrink-0 relative w-[70vw] sm:w-[45vw] md:w-[30vw] lg:w-full 
                snap-center focus:outline-none text-left
              "
            >
              <div className="relative w-full aspect-[3/4] overflow-hidden bg-gray-100">
                <img
                  src={imageUrl}
                  alt={cat.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
              </div>

              <div className="mt-4 pl-1">
                <h3 className="text-sm sm:text-base font-medium tracking-widest text-gray-900 uppercase group-hover:text-black transition-colors">
                  {cat.name.length > 30
                    ? `${cat.name.slice(0, 30)}...`
                    : cat.name}
                </h3>
                <p className="text-xs text-gray-500 mt-1 group-hover:text-gray-700 transition-colors">
                  Shop Now
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  const PALETTE = {
    PRIMARY_BLUE: "#6A9DD5",
    DARK_NAVY: "#1C2E4A",
    ACCENT_BROWN: "#7C4F2B",
    SECONDARY_WHITE: "#EBEFF5",
  };

  const collections = [
    "Forest Edition",
    "Marine Edition",
    "Grand Edition",
    "Celebrity",
    "Formal Edition",
  ];
  const typedCollection = useTypewriter(collections);

  const getAllCategory = async () => {
    try {
      const { data } = await axios.get("/api/v1/category/get-category");
      if (data?.success) setCategories(data?.category || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getAllCategory();
  }, []);

  return (
    <Layout
      title={"Russh Berry | Premium Men's Clothing Brand with Custom Sizes"}
    >
      {/* HERO SECTION */}
      <div className="relative w-full min-h-screen md:h-screen min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Responsive Switch */}
        <div className="absolute inset-0 z-0">
          <picture>
            {/* Mobile Image (max-width: 639px - before sm breakpoint) */}
            <source
              media="(max-width: 639px)"
              srcSet="/images/mobile-hero.png"
            />
            {/* Desktop/Tablet Image (default) */}
            <img
              src="/images/Hero.png"
              alt="banner"
              className="w-full h-full object-cover object-center md:object-[center_top]"
            />
          </picture>
          {/* Mobile: Dark gradient overlay | Tablet/Desktop: Light overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent sm:bg-white/10 md:bg-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          {/* Mobile: Centered | Tablet/Desktop: Left aligned with padding */}
          <div className="max-w-lg md:max-w-xl sm:pt-20 md:pt-0 flex flex-col items-center sm:items-start text-center sm:text-left">
            {/* Brand Name - Only on Mobile */}
            {/* <p className="sm:hidden text-xs tracking-[0.3em] uppercase text-white/90 mb-4 font-light">
              RUSSBERRY & CO.
            </p> */}

            {/* Main Heading */}
            <h1
              className="text-4xl sm:text-4xl md:text-5xl font-serif font-light text-white sm:text-gray-900 mb-4 sm:mb-4 md:mb-6 tracking-wide leading-tight"
              style={{ fontFamily: "'TAN PEARL', serif", lineHeight: "1.4" }}
            >
              {/* Mobile: NEW COLLECTION | Tablet/Desktop: Original text */}
              <span className="sm:hidden">
                Crafted For <br /> Momentum
              </span>
              <span className="hidden sm:inline">
                Discover the Essence <br className="hidden sm:block" /> of
                Timeless Elegance
              </span>
            </h1>

            {/* Description - Hidden on Mobile */}
            <p className="hidden sm:block text-sm sm:text-base md:text-lg text-white sm:text-gray-800 md:text-gray-700 mb-6 md:mb-8 max-w-md leading-relaxed">
              Step into a world where fashion meets art. Explore our exclusive
              collections of timeless elegance and contemporary style.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <button
                onClick={() => {
                  const el = document.querySelector("#shop");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="px-10 py-4 sm:px-8 sm:py-3.5 text-sm font-medium tracking-[0.15em] sm:tracking-normal uppercase bg-white text-black sm:bg-black sm:text-white rounded-sm border border-white sm:border-black transition-all duration-300 hover:bg-transparent hover:text-white sm:hover:bg-transparent sm:hover:text-black shadow-lg hover:shadow-none"
              >
                Shop{" "}
                {/* Mobile: "Shop Now" | Tablet/Desktop: "Shop the Collection" */}
                <span className="sm:inline"> the Collection</span>
              </button>

              {/* Discover More - Hidden on Mobile only */}
              <button
                className="hidden sm:block px-8 py-3.5 text-sm font-medium border border-gray-900 text-gray-900 bg-white/50 backdrop-blur-sm sm:bg-transparent rounded-sm hover:bg-black transition-all duration-300 hover:text-white"
                onClick={() => {
                  const el = document.querySelector("#discover");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Discover More
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* AutoBannerSlider: HIDDEN on Mobile */}
      <div className="hidden md:block">
        <AutoBannerSlider />
      </div>
      {/* MAIN CONTENT AREA */}
      <div className="w-full px-0 sm:px-6 py-12 md:py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-0">
          {/* SECTION HEADER */}
          <div className="text-center mb-10 md:mb-16">
            <h2
              className="text-2xl md:text-3xl font-light tracking-[0.15em] uppercase"
              style={{ color: PALETTE.DARK_NAVY }}
            >
              Curated By The House
            </h2>
            <div
              className="w-16 md:w-24 h-px mx-auto mt-4"
              style={{ backgroundColor: PALETTE.ACCENT_BROWN }}
            />
          </div>

          {/* CONTENT GRID */}
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
            {/* SIDEBAR BANNER (Hidden on Mobile) */}
            <aside className="hidden lg:block w-full lg:w-1/4 sticky top-24 self-start">
              <div className="group cursor-pointer overflow-hidden">
                <div className="relative w-full h-auto overflow-hidden rounded-sm">
                  <img
                    src="/images/founder.jpeg"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    alt="Collection banner"
                  />
                </div>
                <div className="mt-6 text-left">
                  <p className="text-gray-900 text-xs tracking-[0.2em] uppercase font-bold mb-3">
                    Explore Timeless Elegance
                  </p>
                  <p className="text-gray-600 text-base leading-relaxed font-light">
                    Discover our meticulously curated collection, where
                    sophistication meets artistry in every piece.
                  </p>
                </div>
              </div>
            </aside>

            {/* MOBILE BANNER TEXT */}
            <div className="block lg:hidden w-full text-center mb-4 px-4">
              <p className="text-gray-600 text-sm font-light italic">
                "Where sophistication meets artistry in every piece."
              </p>
            </div>

            {/* CATEGORIES GRID */}
            <main className="w-full lg:w-3/4 px-0 sm:px-0" id="shop">
              <FeaturedCategories
                categories={categories}
                onClickCategory={(cat) => navigate(`/category/${cat.slug}`)}
              />
            </main>
          </div>

          {/* PRODUCT SECTIONS */}
          <div className="mt-16 md:mt-24 space-y-16 md:space-y-24">
            <MostViewedProducts id="discover" />

            <JustArrivedProducts limit={6} categories={categories} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;
